import React, { useMemo } from 'react';
import { PaginationItem } from './PaginationItem';
import './styles.scss';

interface PaginationProps {
    totalCountOfRegisters: number;
    registersPerPage?: number;
    currentPage?: number;
    simblingsCount?: number;
    onPageChange: (page: number) => void;
}

function generatePagesArray(from: number, to: number) {
    return [...new Array(to - from)].map((_, index) => from + index + 1).filter((page) => page > 0);
}

const Pagination: React.FC<PaginationProps> = ({
    totalCountOfRegisters,
    registersPerPage = 10,
    currentPage = 1,
    simblingsCount = 1,
    onPageChange,
}: PaginationProps) => {
    const lastPage = useMemo(() => Math.ceil(totalCountOfRegisters / registersPerPage), [totalCountOfRegisters, registersPerPage]);

    const previousPages = useMemo(() => {
        return currentPage > 1 ? generatePagesArray(currentPage - 1 - simblingsCount, currentPage - 1) : [];
    }, [currentPage, simblingsCount]);

    const nextPages = useMemo(() => {
        return currentPage < lastPage ? generatePagesArray(currentPage, Math.min(currentPage + simblingsCount, lastPage)) : [];
    }, [currentPage, simblingsCount, lastPage]);

    return (
        <div className="pagination-container">
            {currentPage > 1 && <PaginationItem isPreview onClick={() => onPageChange(currentPage - 1)} />}

            {currentPage > 1 + simblingsCount && (
                <>
                    <PaginationItem number={1} onClick={() => onPageChange(1)} />
                    {currentPage > 2 + simblingsCount && <span className="ellipsis">...</span>}
                </>
            )}

            {previousPages.length > 0 &&
                previousPages.map((page) => {
                    return <PaginationItem key={page} number={page} onClick={() => onPageChange(page)} />;
                })}

            <PaginationItem isCurrent number={currentPage} />

            {nextPages.length > 0 &&
                nextPages.map((page) => {
                    return <PaginationItem key={page} number={page} onClick={() => onPageChange(page)} />;
                })}

            {currentPage + simblingsCount < lastPage && (
                <>
                    {currentPage + 1 + simblingsCount < lastPage && <span className="ellipsis">...</span>}
                    <PaginationItem number={lastPage} onClick={() => onPageChange(lastPage)} />
                </>
            )}

            {currentPage < lastPage && <PaginationItem isNext onClick={() => onPageChange(currentPage + 1)} />}
        </div>
    );
};

export { Pagination };
